export type PlanId = 'starter' | 'professional' | 'growth'

export const PLAN_NAMES: Record<PlanId, string> = {
  starter: 'Site',
  professional: 'Managed',
  growth: 'Growth',
}

/** Monthly subscription price, in whole dollars. Mirrors Stripe price IDs. */
export const MONTHLY: Record<PlanId, number> = {
  starter: 99,
  professional: 199,
  growth: 399,
}

/** Monthly subscription price in cents, derived so dollars and cents cannot drift. */
export const MONTHLY_CENTS: Record<PlanId, number> = {
  starter: MONTHLY.starter * 100,
  professional: MONTHLY.professional * 100,
  growth: MONTHLY.growth * 100,
}

export const PLAN_ORDER: PlanId[] = ['starter', 'professional', 'growth']
export const POPULAR_PLAN: PlanId = 'professional'

/**
 * There is no minimum term. Plans are month to month: cancel any time and the
 * domain and site files go with the client. (A 3-month minimum used to appear
 * in copy here — it was never enforced in Stripe or in code, and it made the
 * first "yes" cost 3x more than the price on the card.)
 *
 * Nor is there a setup fee — the marketing site used to compute one per trade
 * and city ($99–$499) and nothing in Stripe ever charged it.
 */

export interface PlanDef {
  id: PlanId
  name: string
  tagline: string
  monthly: number
  popular: boolean
  /**
   * Whether this tier appears on public-facing plan pickers (invite plan
   * cards, the marketing pricing page). `false` keeps the plan real —
   * still billable, still a valid Stripe price, still what a manual or
   * existing checkout can select — it just is not offered on the cold
   * ladder. See `PUBLIC_PLANS`.
   */
  public: boolean
  /**
   * A genuinely committed future monthly price, in whole dollars, shown
   * struck through next to `monthly`. Only set this when a higher price
   * has actually been decided and existing subscribers will be
   * grandfathered at the current rate — never to imply a discount from a
   * price that was never charged. Most tiers have no anchor.
   */
  anchorMonthly?: number
  /** Cumulative feature list for this tier (each tier includes the ones below it). */
  features: string[]
}

/**
 * Badge copy for a plan carrying an `anchorMonthly`. Deliberately framed as
 * an early/founding rate, not a discount off a former price — there is no
 * former price, only a committed future one.
 */
export const FOUNDING_PRICING_BADGE = 'Founding pricing'

/**
 * The website is the smallest part of what the monthly buys, and a list that
 * only describes the website makes $99–$399 look like a rented page. Every tier
 * therefore names the two things that actually recur: a person who answers, and
 * the upkeep nobody sees until it breaks.
 */
export const STARTER_FEATURES = [
  'Professional multi-page website',
  'Custom domain + hosting + SSL',
  'Unlimited change requests — you ask, we edit',
  'A real person answers, from your dashboard',
  'Hosting, updates, backups and uptime handled',
  'Call tracking number + analytics',
  'Callback request form',
  'Contact form + click-to-call',
  'Basic on-page SEO',
]
export const PROFESSIONAL_ADDS = [
  'Priority replies — your messages go first',
  'New pages and sections on request',
  'Advanced + local SEO',
  'Logo design or optimization',
  'Google Business Profile setup',
  'Automated review requests',
  'Monthly content updates',
  'Monthly report — site visits and form leads',
]
/**
 * Growth exists to bring the phone more calls, not to add more site. Every
 * line is a lead-generation deliverable the operator actually runs on a
 * schedule — no invented stats, no tools that are not actually in the tier
 * (no AI receptionist, no Jobber/Housecall Pro integration, no Google Ads
 * management, no GBP auto-posting).
 */
export const GROWTH_ADDS = [
  'Review funnel — SMS and email after every job, feedback routed to us before it goes public',
  'Missed-call text-back — an automatic text reply when a call goes unanswered',
  'Local SEO content, on a schedule — new service-area pages, cost guides and blog posts',
  'Extended monthly report — adds reviews and local search rankings to your visits and leads',
]

/**
 * The part of the monthly that is not the website itself, in the order people
 * ask about it. Rendered next to the plan cards wherever price is shown, so the
 * answer to "why so much for a website?" is on the same screen as the price.
 *
 * The first three apply from Managed up — the site is handled. The last two
 * split on the Managed/Growth line: Managed keeps the site found, Growth adds
 * the work that turns that traffic into booked jobs.
 */
export const WHAT_THE_MONTHLY_BUYS: { title: string; body: string }[] = [
  {
    title: 'Someone who picks up',
    body: 'Message us from the dashboard and a person — not a ticket queue — answers and makes the change.',
  },
  {
    title: 'Changes, as often as you need',
    body: 'New prices, a service you dropped, a photo of last week’s job. You send it, we edit it. No hourly rate.',
  },
  {
    title: 'The upkeep you never see',
    body: 'Hosting, the certificate, the domain renewal, updates and monitoring. If it goes down, that is our problem.',
  },
  {
    title: 'Work that keeps it found',
    body: 'Local SEO, your Google profile and monthly content updates — the part that keeps the site visible after launch.',
  },
  {
    title: 'Work that turns visits into calls',
    body: 'On Growth: a review funnel, a text-back for missed calls, scheduled local SEO content and an extended monthly report — the part that turns traffic into booked jobs.',
  },
]

export const PLANS: PlanDef[] = [
  {
    id: 'starter',
    name: PLAN_NAMES.starter,
    tagline: 'Get online fast',
    monthly: MONTHLY.starter,
    popular: false,
    // Non-public downsell: still a valid plan for manual/existing checkouts
    // (see apps/client-portal/backend/src/config/plans.ts), just not offered
    // on the cold ladder. Public tiers are Managed and Growth only.
    public: false,
    features: STARTER_FEATURES,
  },
  {
    id: 'professional',
    name: PLAN_NAMES.professional,
    tagline: 'Best value for most businesses',
    monthly: MONTHLY.professional,
    popular: true,
    public: true,
    // Committed future price — Managed is genuinely moving to $299/mo later,
    // with existing clients grandfathered at $199. Not a fabricated "was" price.
    anchorMonthly: 299,
    features: [...STARTER_FEATURES, ...PROFESSIONAL_ADDS],
  },
  {
    id: 'growth',
    name: PLAN_NAMES.growth,
    tagline: 'For serious growth',
    monthly: MONTHLY.growth,
    popular: false,
    public: true,
    features: [...STARTER_FEATURES, ...PROFESSIONAL_ADDS, ...GROWTH_ADDS],
  },
]

/**
 * The cold, public-facing ladder — what invite plan cards and the marketing
 * pricing page render. Currently Managed and Growth; Site (`starter`) stays
 * in `PLANS` as a real, billable, non-public downsell. Consumers should
 * filter on this rather than hardcoding which ids are public.
 */
export const PUBLIC_PLANS: PlanDef[] = PLANS.filter((plan) => plan.public)

/** Ordered union of every feature across all tiers (Growth's full cumulative list). */
export const ALL_FEATURES: string[] = [...STARTER_FEATURES, ...PROFESSIONAL_ADDS, ...GROWTH_ADDS]

/** ALL_FEATURES mapped to { text, included } for one plan — for the comparison table. */
export function planFeatures(id: PlanId): { text: string; included: boolean }[] {
  const plan = PLANS.find((p) => p.id === id)
  const owned = new Set(plan?.features ?? [])
  return ALL_FEATURES.map((text) => ({ text, included: owned.has(text) }))
}

// Pricing cards show only the tier's own additions ("Everything in X, plus:") —
// the full matrix lives in the comparison table below the cards.
export const CARD_FEATURES: Record<PlanId, { label: string | null; items: string[] }> = {
  starter: { label: null, items: STARTER_FEATURES },
  professional: { label: 'What’s included:', items: PROFESSIONAL_ADDS },
  growth: { label: 'Everything in Managed, plus:', items: GROWTH_ADDS },
}
